import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sparkles, Info } from "lucide-react";
import type { Budget, BudgetDocumentMode } from "@/hooks/useBudgets";
import {
  useUpdateBudgetDocument,
  useSwitchBudgetDocumentMode,
  useUploadBudgetSourcePdf,
  useBudgetFields,
  useSaveBudgetFields,
} from "@/hooks/useBudgetDocument";
import { buildProposalMarkdown } from "@/lib/proposalMarkdown";
import { isProposalDocumentReady, resolveProposalPdf } from "@/lib/proposalDocument";
import { ProposalPdfButton } from "./ProposalPdfButton";
import { DocumentModeCards, type DocumentModeOption } from "../documents/DocumentModeCards";
import { DocumentRichTextAuthor } from "../documents/DocumentRichTextAuthor";
import { PdfUploadStep } from "../documents/PdfUploadStep";
import { PdfFieldEditor } from "../documents/PdfFieldEditor";
import { ClientSignersEditor } from "../documents/ClientSignersEditor";
import type { ClientSigner } from "../documents/types";

const MODES: DocumentModeOption<BudgetDocumentMode>[] = [
  {
    id: "auto",
    title: "Automático",
    desc: "PDF gerado dos itens e valores ao enviar — sem preencher nada",
  },
  {
    id: "richtext",
    title: "Editor de texto",
    desc: "Escreva a proposta aqui, começando de um modelo gerado dos itens",
  },
  {
    id: "pdf_upload",
    title: "Enviar PDF pronto",
    desc: "Marque os campos de assinatura sobre o arquivo",
  },
];

function hasDocumentContent(budget: Budget): boolean {
  return !!budget.richtext_content?.trim() || !!budget.source_pdf_url;
}

/**
 * The proposal's document layer — the same shape as ContractWorkspace so the
 * two flows look and behave alike. Sits below the pricing editor; the
 * numbers stay in budget_items, this is what the client reads and signs.
 */
export function ProposalDocumentWorkspace({
  budget,
  leadName,
  locked,
}: {
  budget: Budget;
  leadName: string;
  locked: boolean;
}) {
  const updateDoc = useUpdateBudgetDocument();
  const switchMode = useSwitchBudgetDocumentMode();
  const upload = useUploadBudgetSourcePdf();
  const { data: fields, isLoading: fieldsLoading } = useBudgetFields(budget.id);
  const saveFields = useSaveBudgetFields(budget.id);
  const [pendingSwitch, setPendingSwitch] = useState<BudgetDocumentMode | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState<((md: string) => void) | null>(null);

  const mode = budget.document_mode ?? "auto";
  const ready = isProposalDocumentReady(budget);
  const pdfUrl = resolveProposalPdf(budget).url;
  const showEditor = !locked && (editing || !ready);

  // Who the client-side signature fields (below) are assigned to. Configured
  // here, ahead of placing fields, so the field editor can offer a real slot
  // per signer instead of the lead's name pointing at the wrong order_index
  // (order_index 0 is always the pre-signed admin — send-budget-signature).
  const [clientSigners, setClientSigners] = useState<ClientSigner[]>(
    budget.pending_client_signers?.length
      ? budget.pending_client_signers
      : [{ name: leadName, email: budget.lead?.email ?? "" }],
  );
  useEffect(() => {
    setClientSigners(
      budget.pending_client_signers?.length
        ? budget.pending_client_signers
        : [{ name: leadName, email: budget.lead?.email ?? "" }],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget.id]);
  const signerLabels = [
    "Você (pré-assinado)",
    ...clientSigners.map((s, i) => s.name.trim() || `Cliente ${i + 1}`),
  ];

  const generateMarkdown = () => buildProposalMarkdown(budget, leadName);

  return (
    <section className="flex flex-col gap-[18px] rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-[15px] font-bold">Documento da proposta</h2>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            O que o cliente lê e assina. Os valores vêm sempre dos itens acima; aqui você escolhe
            como apresentá-los.
          </p>
        </div>
        {/* The only PDF button of the proposal: signed > frozen > uploaded > generated (auto). */}
        <ProposalPdfButton budget={budget} className="h-8 text-xs" />
      </div>

      <DocumentModeCards
        modes={MODES}
        selected={mode}
        disabled={locked || switchMode.isPending}
        onPick={(m) => {
          if (m === mode) return;
          if (hasDocumentContent(budget)) setPendingSwitch(m);
          else {
            switchMode.mutate({ id: budget.id, mode: m });
            setEditing(true);
          }
        }}
      />

      {mode === "auto" && (
        <div className="flex items-start gap-2 rounded-xl border border-border bg-muted/60 px-3.5 py-2.5 text-[12.5px] text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />O PDF é montado automaticamente a partir dos
          itens, escopo e valores no momento do envio.
        </div>
      )}

      {mode === "richtext" &&
        (showEditor ? (
          <DocumentRichTextAuthor
            documentId={budget.id}
            content={budget.richtext_content}
            saving={updateDoc.isPending}
            onSave={(md) =>
              updateDoc.mutate(
                { id: budget.id, data: { richtext_content: md } },
                { onSuccess: () => setEditing(false) },
              )
            }
            hint="Redija a proposta: escopo, tabela de investimento, o que está incluso, prazo e validade. As assinaturas são adicionadas automaticamente ao final ao enviar."
            placeholder="Redija a proposta..."
            extraActions={(setDraft, draft) => (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  draft.trim() ? setConfirmGenerate(() => setDraft) : setDraft(generateMarkdown())
                }
              >
                <Sparkles className="mr-1.5 h-4 w-4" /> Gerar a partir dos itens
              </Button>
            )}
          />
        ) : budget.richtext_content ? (
          <div className="prose prose-sm max-h-[320px] max-w-none overflow-y-auto rounded-[13px] border border-border bg-muted px-5 py-[18px] text-[13px] leading-[1.75] text-muted-foreground [&_h1]:text-[13px] [&_h1]:font-bold [&_h1]:text-foreground [&_h2]:text-[13px] [&_h2]:font-bold [&_h2]:text-foreground [&_h3]:text-[13px] [&_h3]:font-bold [&_h3]:text-foreground [&_strong]:text-foreground [&_table]:text-[12px]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{budget.richtext_content}</ReactMarkdown>
          </div>
        ) : null)}

      {mode === "pdf_upload" &&
        (showEditor ? (
          !budget.source_pdf_url ? (
            <PdfUploadStep
              uploading={upload.isPending}
              hint="Arraste o PDF da proposta aqui ou"
              onFile={async (file) => {
                const url = await upload.mutateAsync({ budgetId: budget.id, file });
                updateDoc.mutate({ id: budget.id, data: { source_pdf_url: url } });
              }}
            />
          ) : (
            <div className="space-y-3">
              <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-3">
                <ClientSignersEditor value={clientSigners} onChange={setClientSigners} />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={updateDoc.isPending}
                  onClick={() =>
                    updateDoc.mutate({
                      id: budget.id,
                      data: { pending_client_signers: clientSigners },
                    })
                  }
                >
                  Salvar signatários
                </Button>
              </div>
              <PdfFieldEditor
                sourcePdfUrl={budget.source_pdf_url}
                signerLabels={signerLabels}
                fields={fields}
                fieldsLoading={fieldsLoading}
                onSave={(f) => saveFields.mutate(f, { onSuccess: () => setEditing(false) })}
                saving={saveFields.isPending}
              />
            </div>
          )
        ) : pdfUrl ? (
          <div className="overflow-hidden rounded-[13px] border border-border bg-muted">
            <iframe src={pdfUrl} title="Proposta" className="h-[320px] w-full" />
          </div>
        ) : null)}

      {mode !== "auto" && !locked && ready && !editing && (
        <div className="-mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEditing(true)}
          >
            {mode === "richtext" ? "Editar texto" : "Editar campos"}
          </Button>
        </div>
      )}

      <AlertDialog
        open={!!pendingSwitch}
        onOpenChange={(o) => {
          if (!o) setPendingSwitch(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Trocar tipo de documento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {mode === "richtext"
                ? "O texto redigido até agora será descartado."
                : "O PDF enviado e os campos posicionados serão descartados."}{" "}
              Os itens e valores da proposta não mudam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                if (pendingSwitch) switchMode.mutate({ id: budget.id, mode: pendingSwitch });
                setPendingSwitch(null);
                setEditing(true);
              }}
            >
              Trocar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmGenerate}
        onOpenChange={(o) => {
          if (!o) setConfirmGenerate(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Substituir o texto atual?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              O conteúdo do editor será trocado pelo modelo gerado dos itens. Você ainda pode
              desfazer com Ctrl+Z antes de salvar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                confirmGenerate?.(generateMarkdown());
                setConfirmGenerate(null);
              }}
            >
              Substituir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
